import React, { useState, useEffect } from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import api from '../../../../middleware/api';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuth } from '../../../../context/AuthContext';
import MigrationPopup from '../../MigrationPopup';

const MIGRATION_POPUP_KEY = 'verbatim_migration_popup_shown';

const LoginForm = () => {
    const navigateTo = useNavigate();
    const { setUser } = useAuth();
    const [showMigrationPopup, setShowMigrationPopup] = useState(false);
    
    useEffect(() => {
        // Vérifier si le popup a déjà été affiché
        const popupShown = localStorage.getItem(MIGRATION_POPUP_KEY);
        if (!popupShown) {
            setShowMigrationPopup(true);
        }
    }, []);
    
    // Validation schema using Yup
    const validationSchema = Yup.object({
        email: Yup.string().email('Adresse email invalide').required('Requis'),
        password: Yup.string().min(6, 'Le mot de passe doit contenir au moins 6 caractères').required('Requis')
    });

    const handleLogin = async (values, { setSubmitting }) => {
        try {
            // Sending login request with email and password
            const email = values.email.toLowerCase().trim();
            const response = await api.post('/auth/login', { email, password: values.password });
            const token = response.data.token;

            if (!token) {
                throw new Error('Token non trouvé');
            } else {
                // Store token in localStorage or cookie
                localStorage.setItem('token', token);
                try {
                    const user = await api.get('/users')
                    if (user.data) {
                        setUser(user.data)
                    }
                } catch (error) {
                    console.error(error)
                }
            }

            // Redirect user to another page
            navigateTo('/');
        } catch (error) {
            toast.error('Identifiants invalides');
        } finally {
            setSubmitting(false);
        }
    };

    const handleForgotPassword = () => {
        navigateTo('/mot-de-passe-oublie');
    }

    return (
        <div className="flex flex-col justify-center items-center w-3/4 max-w-md mx-auto p-8 bg-white rounded-lg shadow-lg">
            {showMigrationPopup && (
                <MigrationPopup onClose={() => setShowMigrationPopup(false)} />
            )}
            <h2 className="text-2xl font-bold text-gray-800 mb-8 relative">
                CONNEXION
                <span className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-16 h-1 bg-primary -mb-3"></span>
            </h2>
            <Formik
                initialValues={{ email: '', password: '', savePassword: false }}
                validationSchema={validationSchema}
                onSubmit={handleLogin}
            >
                {({ isSubmitting }) => (
                    <Form className="w-full flex flex-col items-center space-y-6">
                        <div className="w-full">
                            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                        <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                                        <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                                    </svg>
                                </div>
                                <Field
                                    type="email"
                                    name="email"
                                    className="w-full bg-gray-50 text-gray-700 text-base font-normal pl-10 pr-3 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition"
                                    autoComplete="email"
                                    placeholder="exemple@domaine.com"
                                />
                            </div>
                            <ErrorMessage name="email" component="div" className="text-red-500 text-sm mt-1" />
                        </div>
                        
                        <div className="w-full">
                            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">Mot de passe</label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                                    </svg>
                                </div>
                                <Field
                                    type="password"
                                    name="password"
                                    className="w-full bg-gray-50 text-gray-700 text-base font-normal pl-10 pr-3 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition"
                                    autoComplete="current-password"
                                    placeholder="••••••••"
                                />
                            </div>
                            <ErrorMessage name="password" component="div" className="text-red-500 text-sm mt-1" />
                        </div>
                        
                        <button 
                            type='button' 
                            className="self-start text-sm text-primary-dark hover:text-primary transition-colors" 
                            onClick={handleForgotPassword}
                        >
                            Mot de passe oublié ?
                        </button>
                        
                        <div className="w-full pt-2">
                            <button
                                type="submit"
                                className="w-full flex justify-center py-3 px-4 bg-primary hover:bg-primary-dark text-white font-medium rounded-md shadow-sm transition duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? 'Connexion en cours...' : 'Se connecter'}
                            </button>
                        </div>
                    </Form>
                )}
            </Formik>
        </div>
    );
};

export default LoginForm;
